import { useSite } from "@inrange/building-manager-api-client";
import {
  AllowStringForNumbers,
  FixedTariff,
  RecursivePartial,
  RecursivePartialSiteAllowStringValues,
  SingleRateTimedTariff,
  Site,
  SiteAllowStringValues,
  StandingCharge,
  StandingChargeType,
  SupplierTariffConfig,
  TariffPoint,
  TariffType,
  TimedTariff,
  TimedTariffDays,
  TimedTariffRate,
} from "@inrange/building-manager-api-client/models-site";
import {
  buildSiteCalculationPayload,
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import { BorderedCheckbox } from "@inrange/theme-components";
import { currencySymbol } from "@inrange/theme-components/formatting";
import { Icons } from "@inrange/theme-components/icons";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useReactQueryError } from "../../../utils/react-query-error";
import FormInputPercentage from "../../FormInputPercentage";
import CollapsibleCard from "../CollapsibleCard";
import { PPA_TYPE_LABELS } from "./enums";
import { formatNumber, parseAsNumber } from "./utils";

interface FrontendState {
  isInserted: boolean;
  isNameClash?: boolean;
  insertedIndex: number;
  key: string;
  volumeLabel?: string;
  tariffLabel: string;
}

const getTariffRate = (
  tariff: AllowStringForNumbers<TimedTariff>,
  rateId: string
) => {
  if (tariff.baseRate.id === rateId) {
    return tariff.baseRate;
  }
  return tariff.overrideRatesMap[rateId];
};
const updateTariffRate = (
  tariff: AllowStringForNumbers<TimedTariff>,
  rateId: string,
  updateFn: (
    rate: AllowStringForNumbers<TimedTariffRate>
  ) => RecursivePartial<AllowStringForNumbers<TimedTariffRate>>
): RecursivePartial<AllowStringForNumbers<TimedTariff>> => {
  if (tariff.baseRate.id === rateId) {
    return {
      baseRate: updateFn(tariff.baseRate),
    };
  }
  return {
    overrideRatesMap: {
      [rateId]: updateFn(tariff.overrideRatesMap[rateId]),
    },
  };
};
const getTariffRatePeriodsDescriptionForRateName = (
  tariff: AllowStringForNumbers<TimedTariff>,
  rateId: string
) => {
  const tariffRate = tariff.overrideRatesMap[rateId];
  if (!tariffRate) return undefined;
  return getTariffRatePeriodsDescription(tariffRate);
};
const dayNumberToDateString = (
  dayNumber: number,
  year: number = 2024
): string => {
  const date = new Date(year, 0, dayNumber); // Month 0 = January
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
};
const getTariffRatePeriodsDescription = (
  tariffRate: AllowStringForNumbers<TimedTariffRate>
) => {
  return (
    <>
      {Object.values(tariffRate.rangesMap).map((range, index) => {
        const startHour = Math.floor((range.startIndex as number) / 2)
          .toString()
          .padStart(2, "0");
        const startMinute =
          (range.startIndex as number) % 2 === 0 ? "00" : "30";
        const endHour = Math.floor((range.endIndex as number) / 2)
          .toString()
          .padStart(2, "0");
        const endMinute = (range.endIndex as number) % 2 === 0 ? "00" : "30";
        const dateRange =
          range.startYearDay && range.endYearDay
            ? ` (${dayNumberToDateString(parseAsNumber(range.startYearDay))} - ${dayNumberToDateString(parseAsNumber(range.endYearDay))})`
            : "";
        return (
          <div key={index}>
            {startHour}:{startMinute} - {endHour}:{endMinute} {range.days}
            {dateRange}
          </div>
        );
      })}
    </>
  );
};
const getTariffRateIndex = (
  tariff: AllowStringForNumbers<TimedTariff>,
  rateId: string
) => {
  if (tariff.baseRate.id === rateId) {
    return "baseRate"; // base rate is always at index 0
  }
  return `overrideRatesMap.${rateId}`;
};
const TimedTariffTariffControl = ({
  errors,
  setSite,
  tariffLabel,
  tariffFieldName,
  tariff,
  rateId,
  size = 3,
  showRatePeriods,
}: {
  errors: Record<string, string>;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  tariffLabel: string;
  tariffFieldName: string;
  tariff: AllowStringForNumbers<TimedTariff>;
  rateId: string;
  size?: number;
  showRatePeriods?: boolean;
}) => {
  const errorName = `tariffConfig.supplier.${tariffFieldName}.${getTariffRateIndex(tariff, rateId)}.tariff`;
  return (
    <Col sm={size}>
      <Form.Group
        className="mb-3"
        controlId={`${tariff.tariffName}-${rateId}-tariff`}
      >
        <Form.Label>{tariffLabel}</Form.Label>
        <Form.Control
          type="text"
          value={getTariffRate(tariff, rateId)?.tariff || ""}
          onChange={(e) =>
            setSite({
              tariffConfig: {
                supplier: {
                  [tariffFieldName]: updateTariffRate(tariff, rateId, (_) => {
                    return {
                      tariff: e.target.value,
                    };
                  }),
                },
              },
            })
          }
          isInvalid={!!errors[errorName]}
        />
        {showRatePeriods && (
          <div
            style={{
              fontSize: "0.875em",
              marginTop: "0.25rem",
            }}
          >
            {getTariffRatePeriodsDescriptionForRateName(tariff, rateId)}
          </div>
        )}
        <Form.Control.Feedback type="invalid">
          {errors[errorName]}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );
};

const TimedTariffPeriodControl = ({
  setSite,
  tariffField,
  rateLabel,
  tariff,
  rateId,
  rateRangeId,
  size = 3,
}: {
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  tariffField: string;
  rateLabel: string;
  tariff: AllowStringForNumbers<TimedTariff>;
  rateId: string;
  rateRangeId: string;
  size?: number;
}) => {
  return (
    <>
      <Col sm={size}>
        <Form.Group
          className="mb-3"
          controlId={`${tariff.tariffName}-${rateId}-days`}
        >
          <Form.Label>{rateLabel} days</Form.Label>
          <Form.Select
            value={
              getTariffRate(tariff, rateId)?.rangesMap?.[rateRangeId]?.days
            }
            onChange={(e) => {
              setSite({
                tariffConfig: {
                  supplier: {
                    [tariffField]: updateTariffRate(tariff, rateId, (_) => {
                      return {
                        rangesMap: {
                          [rateRangeId]: {
                            days: e.target.value,
                          },
                        },
                      };
                    }),
                  },
                },
              });
            }}
          >
            <option value={TimedTariffDays.EVERY_DAY}>Every day</option>
            <option value={TimedTariffDays.WEEKDAYS}>Weekdays</option>
            <option value={TimedTariffDays.EXCLUDE_SUNDAYS}>
              Exclude Sundays
            </option>
            <option value={TimedTariffDays.WEEKENDS}>Weekends</option>
          </Form.Select>
        </Form.Group>
      </Col>
      <Col sm={size}>
        <Form.Group
          className="mb-3"
          controlId={`${tariff.tariffName}-${rateId}-hours-start`}
        >
          <Form.Label>{rateLabel} start (incl)</Form.Label>
          <Form.Select
            value={
              getTariffRate(tariff, rateId)!.rangesMap?.[rateRangeId].startIndex
            }
            onChange={(e) =>
              setSite({
                tariffConfig: {
                  supplier: {
                    peakOffPeak: updateTariffRate(tariff, rateId, (_) => {
                      return {
                        rangesMap: {
                          [rateRangeId]: {
                            startIndex: e.target.value,
                          },
                        },
                      };
                    }),
                  },
                },
              })
            }
          >
            {Array.from({ length: 49 }, (_, i) => {
              const hour = Math.floor(i / 2)
                .toString()
                .padStart(2, "0");
              const minute = i % 2 === 0 ? "00" : "30";
              return (
                <option key={i} value={i}>
                  {`${hour}:${minute}`}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>
      </Col>
      <Col sm={size}>
        <Form.Group
          className="mb-3"
          controlId={`${tariff.tariffName}-${rateId}-hours-end`}
        >
          <Form.Label>{rateLabel} end (excl)</Form.Label>
          <Form.Select
            value={
              getTariffRate(tariff, rateId)!.rangesMap[rateRangeId]?.endIndex
            }
            onChange={(e) =>
              setSite({
                tariffConfig: {
                  supplier: {
                    peakOffPeak: updateTariffRate(tariff, rateId, (_) => {
                      return {
                        rangesMap: {
                          [rateRangeId]: {
                            endIndex: e.target.value,
                          },
                        },
                      };
                    }),
                  },
                },
              })
            }
          >
            {Array.from({ length: 49 }, (_, i) => {
              const hour = Math.floor(i / 2)
                .toString()
                .padStart(2, "0");
              const minute = i % 2 === 0 ? "00" : "30";
              return (
                <option key={i} value={i}>
                  {`${hour}:${minute}`}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  );
};

const SingleRateTimedTariffTariffControl = ({
  errors,
  setSite,
  tariffLabel,
  tariff,
  size = 3,
  showRatePeriods,
}: {
  errors: Record<string, string>;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  tariffLabel: string;
  tariff: AllowStringForNumbers<SingleRateTimedTariff>;
  size?: number;
  showRatePeriods?: boolean;
}) => {
  const errorName = `tariffConfig.supplier.standardSingleRateTimedTariffs.${tariff.tariffName}.rate.tariff`;
  return (
    <Col sm={size}>
      <Form.Group className="mb-3" controlId={`${tariff.tariffName}-tariff`}>
        <Form.Label>{tariffLabel}</Form.Label>
        <Form.Control
          type="text"
          value={tariff.rate.tariff || ""}
          onChange={(e) =>
            setSite({
              tariffConfig: {
                supplier: {
                  standardSingleRateTimedTariffs: {
                    [tariff.tariffName]: {
                      rate: {
                        tariff: e.target.value,
                      },
                    },
                  },
                },
              },
            })
          }
          isInvalid={!!errors[errorName]}
        />
        {showRatePeriods && (
          <div
            style={{
              fontSize: "0.875em",
              marginTop: "0.25rem",
            }}
          >
            {getTariffRatePeriodsDescription(tariff.rate)}
          </div>
        )}
        <Form.Control.Feedback type="invalid">
          {errors[errorName]}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );
};

const FixedTariffTariffControl = ({
  tariff,
  label,
  setSite,
  size = 4,
}: {
  tariff: AllowStringForNumbers<FixedTariff>;
  label: string;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  size?: number;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const errorName = `tariffConfig.supplier.standardFixedTariffs.${tariff.tariffName}.tariff`;
  return (
    <Col sm={size}>
      <Form.Group className="mb-3" controlId={`${tariff.tariffName}-tariff`}>
        <Form.Label>
          {label.replace("£", currencySymbol(siteCalculations.currencyCode))}
        </Form.Label>
        <Form.Control
          type="text"
          value={tariff.tariff || ""}
          onChange={(e) =>
            setSite({
              tariffConfig: {
                supplier: {
                  standardFixedTariffs: {
                    [tariff.tariffName]: {
                      ...tariff,
                      tariff: e.target.value,
                    },
                  },
                },
              },
            })
          }
          isInvalid={!!errors[errorName]}
        />
        <Form.Control.Feedback type="invalid">
          {errors[errorName]}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );
};

const StandingChargeVolumeControl = ({
  standingCharge,
  label,
  setSite,
  size = 4,
}: {
  standingCharge: AllowStringForNumbers<StandingCharge>;
  label: string;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  size?: number;
}) => {
  const { errors } = useContextTS(SiteCalculationsContext);
  const errorName = `tariffConfig.supplier.standardStandingCharges.${standingCharge.chargeName}.volume`;
  return (
    <Col sm={size}>
      <Form.Group
        className="mb-3"
        controlId={`${standingCharge.chargeName}-volume`}
      >
        <Form.Label>{label}</Form.Label>
        <Form.Control
          type="text"
          value={standingCharge.volume || ""}
          onChange={(e) =>
            setSite({
              tariffConfig: {
                supplier: {
                  standardStandingCharges: {
                    [standingCharge.chargeName]: {
                      ...standingCharge,
                      volume: e.target.value,
                    },
                  },
                },
              },
            })
          }
          isInvalid={!!errors[errorName]}
        />
        <Form.Control.Feedback type="invalid">
          {errors[errorName]}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );
};

const StandingChargeTariffControl = ({
  standingCharge,
  label,
  setSite,
  size = 4,
}: {
  standingCharge: AllowStringForNumbers<StandingCharge>;
  label: string;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  size?: number;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const errorName = `tariffConfig.supplier.standardStandingCharges.${standingCharge.chargeName}.tariff`;
  return (
    <Col sm={size}>
      <Form.Group
        className="mb-3"
        controlId={`${standingCharge.chargeName}-tariff`}
      >
        <Form.Label>
          {label.replace("£", currencySymbol(siteCalculations.currencyCode))}
        </Form.Label>
        <Form.Control
          type="text"
          value={standingCharge.tariff || ""}
          onChange={(e) =>
            setSite({
              tariffConfig: {
                supplier: {
                  standardStandingCharges: {
                    [standingCharge.chargeName]: {
                      ...standingCharge,
                      tariff: e.target.value,
                    },
                  },
                },
              },
            })
          }
          isInvalid={!!errors[errorName]}
        />
        <Form.Control.Feedback type="invalid">
          {errors[errorName]}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );
};

const TariffPointFactorControl = ({
  setSite,
  errors,
  supplier,
  label,
  tariffPointFieldName,
  size = 4,
}: {
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  errors: Record<string, string>;
  supplier: AllowStringForNumbers<SupplierTariffConfig>;
  label: string;
  tariffPointFieldName: string;
  size?: number;
}) => {
  const errorName = `tariffConfig.supplier.${tariffPointFieldName}`;
  return (
    <Col sm={size}>
      <Form.Group
        className="mb-3"
        controlId={`tariffPointFactors-${tariffPointFieldName}`}
      >
        <Form.Label>{label}</Form.Label>
        <Form.Control
          type="text"
          value={supplier[tariffPointFieldName] || ""}
          onChange={(e) =>
            setSite({
              tariffConfig: {
                supplier: {
                  [tariffPointFieldName]: e.target.value,
                },
              },
            })
          }
          isInvalid={!!errors[errorName]}
        />
        <Form.Control.Feedback type="invalid">
          {errors[errorName]}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );
};

const CustomFixedTariffControl = ({
  tariff,
  tariffState,
  setTariff,
  removeFixedTariff,
}: {
  tariff: AllowStringForNumbers<FixedTariff>;
  tariffState: FrontendState;
  setTariff: (tariff: AllowStringForNumbers<FixedTariff>) => void;
  removeFixedTariff: () => void;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const errorPrefix = tariffState.isNameClash
    ? ""
    : `tariffConfig.supplier.customFixedTariffs.${tariffState.insertedIndex}`;
  const colWidth =
    tariff.tariffPoint === undefined || tariff.tariffPoint === null ? 2 : 4;
  return (
    <>
      <Col sm={4}>
        <Form.Group
          className="mb-3"
          controlId={`customFixedTariff-${tariffState.key}-name`}
        >
          <Form.Label>Custom charge name</Form.Label>
          <Form.Control
            type="text"
            value={tariff.tariffName}
            onChange={(e) =>
              setTariff({ ...tariff, tariffName: e.target.value })
            }
            isInvalid={
              !!errors[`${errorPrefix}.tariffName`] || !!tariffState.isNameClash
            }
          />
          <Form.Control.Feedback type="invalid">
            {(tariffState.isNameClash && "Duplicate name") ||
              errors[`${errorPrefix}.tariffName`]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col sm={colWidth}>
        <Form.Group
          className="mb-3"
          controlId={`customFixedTariff-${tariffState.key}-tariff`}
        >
          <Form.Label>
            Charge ({currencySymbol(siteCalculations.currencyCode)}/kWh)
          </Form.Label>
          <Form.Control
            type="text"
            value={tariff.tariff}
            onChange={(e) => setTariff({ ...tariff, tariff: e.target.value })}
            isInvalid={!!errors[`${errorPrefix}.tariff`]}
          />
          <Form.Control.Feedback type="invalid">
            {errors[`${errorPrefix}.tariff`]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      {(tariff.tariffPoint === undefined || tariff.tariffPoint === null) && (
        <Col sm={colWidth}>
          <Form.Group
            className="mb-3"
            controlId={`customFixedTariff-${tariffState.key}-volume`}
          >
            <Form.Label>Volume (kWh/yr)</Form.Label>
            <Form.Control
              type="text"
              value={tariff.volume || ""}
              onChange={(e) => setTariff({ ...tariff, volume: e.target.value })}
              isInvalid={!!errors[`${errorPrefix}.volume`]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`${errorPrefix}.volume`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      )}
      <Col sm={4}>
        <Form.Group
          className="mb-3"
          controlId={`customFixedTariff-${tariffState.key}-appliedAt`}
          style={{
            display: "grid",
            gridTemplateColumns: "auto 32px",
            gridTemplateRows: "auto auto",
            gap: "0 8px",
          }}
        >
          <Form.Label style={{ gridRow: 1, gridColumn: "span 2" }}>
            Applied at
          </Form.Label>
          <Form.Select
            value={tariff.tariffPoint || ""}
            style={{ gridRow: 2, gridColumn: 1 }}
            onChange={(e) =>
              setTariff(
                e.target.value === ""
                  ? {
                      ...tariff,
                      tariffPoint: null as unknown as undefined,
                    }
                  : {
                      ...tariff,
                      tariffPoint: e.target.value,
                      volume: undefined,
                    }
              )
            }
          >
            <option value={TariffPoint.MSP}>MSP</option>
            <option value={TariffPoint.GSP}>GSP</option>
            <option value={TariffPoint.NBP}>NBP</option>
            <option value={""}>Manual</option>
          </Form.Select>
          <div
            onClick={removeFixedTariff}
            style={{
              gridRow: 2,
              gridColumn: 2,
              cursor: "pointer",
              alignSelf: "center",
            }}
          >
            <img
              alt="remove"
              src={Icons.trashcanRed}
              style={{
                width: "20px",
                height: "20px",
                marginLeft: "6px",
                cursor: "pointer",
              }}
            ></img>
          </div>
        </Form.Group>
      </Col>
    </>
  );
};

const CustomStandingChargeControl = ({
  standingCharge,
  standingChargeState,
  setStandingCharge,
  removeStandingCharge,
}: {
  standingCharge: AllowStringForNumbers<StandingCharge>;
  standingChargeState: FrontendState;
  setStandingCharge: (tariff: AllowStringForNumbers<StandingCharge>) => void;
  removeStandingCharge: () => void;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);
  const errorPrefix = standingChargeState.isNameClash
    ? ""
    : `tariffConfig.supplier.customStandingCharges.${standingChargeState.insertedIndex}`;
  return (
    <>
      <Col sm={4}>
        <Form.Group
          className="mb-3"
          controlId={`customStandingCharge-${standingChargeState.key}-name`}
        >
          <Form.Label>Custom charge name</Form.Label>
          <Form.Control
            type="text"
            value={standingCharge.chargeName}
            onChange={(e) =>
              setStandingCharge({
                ...standingCharge,
                chargeName: e.target.value,
              })
            }
            isInvalid={
              !!errors[`${errorPrefix}.chargeName`] ||
              !!standingChargeState.isNameClash
            }
          />
          <Form.Control.Feedback type="invalid">
            {(standingChargeState.isNameClash && "Duplicate name") ||
              errors[`${errorPrefix}.chargeName`]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col sm={4}>
        <Form.Group
          className="mb-3"
          controlId={`customFixedTariff-${standingChargeState.key}-tariff`}
        >
          <Form.Label>
            Custom charge ({currencySymbol(siteCalculations.currencyCode)}/kWh)
          </Form.Label>
          <Form.Control
            type="text"
            value={standingCharge.tariff}
            onChange={(e) =>
              setStandingCharge({
                ...standingCharge,
                tariff: e.target.value,
              })
            }
            isInvalid={!!errors[`${errorPrefix}.tariff`]}
          />
          <Form.Control.Feedback type="invalid">
            {errors[`${errorPrefix}.tariff`]}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col sm={4}>
        <Form.Group
          className="mb-3"
          controlId={`customStandingCharge-${standingChargeState.key}-unit`}
          style={{
            display: "grid",
            gridTemplateColumns: "auto 32px",
            gridTemplateRows: "auto auto",
            gap: "0 8px",
          }}
        >
          <Form.Label style={{ gridRow: 1, gridColumn: "span 2" }}>
            Custom unit
          </Form.Label>
          <Form.Select
            value={standingCharge.chargeType}
            style={{ gridRow: 2, gridColumn: 1 }}
            onChange={(e) =>
              setStandingCharge({
                ...standingCharge,
                chargeType: e.target.value as StandingChargeType,
              })
            }
          >
            <option value={StandingChargeType.PER_DAY}>Daily</option>
            <option value={StandingChargeType.PER_MONTH}>Monthly</option>
          </Form.Select>
          <div
            onClick={removeStandingCharge}
            style={{
              gridRow: 2,
              gridColumn: 2,
              cursor: "pointer",
              alignSelf: "center",
            }}
          >
            <img
              alt="remove"
              src={Icons.trashcanRed}
              style={{
                width: "20px",
                height: "20px",
                marginLeft: "6px",
                cursor: "pointer",
              }}
            ></img>
          </div>
        </Form.Group>
      </Col>
    </>
  );
};

const SupplierTariff = ({
  site,
  setSite,
  isBulkEdit,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
  isBulkEdit?: boolean;
}) => {
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string>("1");
  const [customFixedTariffs, setCustomFixedTariffs] = useState<
    AllowStringForNumbers<FixedTariff>[]
  >(site.tariffConfig.supplier.customFixedTariffs);
  const [customFixedTariffsState, setCustomFixedTariffsState] = useState<
    FrontendState[]
  >(
    site.tariffConfig.supplier.customFixedTariffs.map((_, index) => {
      return {
        isInserted: true,
        insertedIndex: index,
        key: `custom-${index}`,
        tariffLabel: "custom",
      };
    })
  );
  const [customStandingCharges, setCustomStandingCharges] = useState<
    AllowStringForNumbers<StandingCharge>[]
  >(site.tariffConfig.supplier.customStandingCharges);
  const [customStandingChargesState, setCustomStandingChargesState] = useState<
    FrontendState[]
  >(
    site.tariffConfig.supplier.customStandingCharges.map((_, index) => {
      return {
        isInserted: true,
        insertedIndex: index,
        key: `custom-charge-${index}`,
        tariffLabel: "custom",
      };
    })
  );
  const { fetchSiteReportPreview } = useSite({
    app: "admin",
  });
  useReactQueryError(fetchSiteReportPreview, "Error downloading report");
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);

  useEffect(() => {
    const seenTariffNames = new Set();
    const insertedCustomFixedTariffs: AllowStringForNumbers<FixedTariff>[] = [];
    const updatedCustomFixedTariffsState = [...customFixedTariffsState];
    for (let i = 0; i < updatedCustomFixedTariffsState.length; i++) {
      const customTariff = customFixedTariffs[i];
      if (
        !!customTariff.tariffName &&
        !!customTariff.tariff &&
        (!!customTariff.tariffPoint || !!customTariff.volume) &&
        !seenTariffNames.has(customTariff.tariffName)
      ) {
        seenTariffNames.add(customTariff.tariffName);
        insertedCustomFixedTariffs.push(customTariff);
        updatedCustomFixedTariffsState[i] = {
          ...updatedCustomFixedTariffsState[i],
          isNameClash: false,
          isInserted: true,
          insertedIndex: insertedCustomFixedTariffs.length - 1,
        };
      } else {
        updatedCustomFixedTariffsState[i] = {
          ...updatedCustomFixedTariffsState[i],
          isNameClash: seenTariffNames.has(customTariff.tariffName),
          isInserted: false,
          insertedIndex: -1,
        };
      }
    }
    if (!_.isEqual(updatedCustomFixedTariffsState, customFixedTariffsState)) {
      setCustomFixedTariffsState(updatedCustomFixedTariffsState);
    }
    const seenChargeNames = new Set();
    const insertedCustomStandingCharges: AllowStringForNumbers<StandingCharge>[] =
      [];
    const updatedCustomStandingChargesState = [...customStandingChargesState];
    for (let i = 0; i < updatedCustomStandingChargesState.length; i++) {
      const customStandingCharge = customStandingCharges[i];
      if (
        !!customStandingCharge.chargeName &&
        !!customStandingCharge.tariff &&
        (!updatedCustomStandingChargesState[i].volumeLabel ||
          !!customStandingCharge.volume) &&
        !seenChargeNames.has(customStandingCharge.chargeName)
      ) {
        seenChargeNames.add(customStandingCharge.chargeName);
        insertedCustomStandingCharges.push(customStandingCharge);
        updatedCustomStandingChargesState[i] = {
          ...updatedCustomStandingChargesState[i],
          isNameClash: false,
          isInserted: true,
          insertedIndex: insertedCustomStandingCharges.length - 1,
        };
      } else {
        updatedCustomStandingChargesState[i] = {
          ...updatedCustomStandingChargesState[i],
          isNameClash: seenChargeNames.has(customStandingCharge.chargeName),
          isInserted: false,
          insertedIndex: -1,
        };
      }
    }
    if (
      !_.isEqual(updatedCustomStandingChargesState, customStandingChargesState)
    ) {
      setCustomStandingChargesState(updatedCustomStandingChargesState);
    }
    const customFixedTariffsChanged = !_.isEqual(
      site.tariffConfig.supplier.customFixedTariffs,
      insertedCustomFixedTariffs
    );
    const customStandingChargesChanged = !_.isEqual(
      site.tariffConfig.supplier.customStandingCharges,
      insertedCustomStandingCharges
    );
    if (customFixedTariffsChanged || customStandingChargesChanged) {
      setSite({
        tariffConfig: {
          supplier: {
            ...(customFixedTariffsChanged
              ? { customFixedTariffs: insertedCustomFixedTariffs }
              : {}),
            ...(customStandingChargesChanged
              ? { customStandingCharges: insertedCustomStandingCharges }
              : {}),
          },
        },
      });
    }
  }, [
    site,
    setCustomStandingCharges,
    setSite,
    customFixedTariffs,
    customStandingCharges,
    customFixedTariffsState,
    customStandingChargesState,
  ]);
  const isMarketPricePeakOffPeak =
    site.tariffConfig.supplier.peakOffPeak.tariffType === TariffType.MARKET;
  return (
    <CollapsibleCard
      title={"Supplier tariffs, charges, and savings"}
      className="mt-2"
      isBulkEdit={isBulkEdit}
    >
      <Row className="d-flex flex-wrap align-items-end">
        {!isMarketPricePeakOffPeak && (
          <TimedTariffTariffControl
            errors={errors}
            setSite={setSite}
            tariffLabel={`Peak/day/MSP (${currencySymbol(siteCalculations.currencyCode)}/kWh)`}
            tariffFieldName="peakOffPeak"
            tariff={site.tariffConfig.supplier.peakOffPeak}
            rateId="peak"
          />
        )}
        {isMarketPricePeakOffPeak && (
          <Col sm={3}>
            <Form.Group
              className="mb-3"
              controlId={`timedImportPriceWeightedAverageTariff`}
            >
              <Form.Label>{`Peak/day/MSP (${currencySymbol(siteCalculations.currencyCode)}/kWh)`}</Form.Label>
              <Form.Control
                type="text"
                value={
                  site.tariffConfig.supplier.peakOffPeak.overrideRatesMap[
                    "peak"
                  ]?.tariff ?? 0
                }
                disabled={true}
              />
            </Form.Group>
          </Col>
        )}
        <Col sm={3}>
          <Form.Group className="mb-3" controlId="supplier">
            <Form.Label>Supplier</Form.Label>
            <Form.Control
              type="text"
              value={site.tariffConfig.supplier.supplier}
              onChange={(e) =>
                setSite({
                  tariffConfig: {
                    ...site.tariffConfig!,
                    supplier: {
                      ...site.tariffConfig!.supplier,
                      supplier: e.target.value,
                    },
                  },
                })
              }
              isInvalid={!!errors[`tariffConfig.supplier.supplier`]}
            />
            <Form.Control.Feedback type="invalid">
              {errors[`tariffConfig.supplier.supplier`]}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        {!isMarketPricePeakOffPeak && (
          <>
            <Col sm={3}>
              <Form.Group className="mb-3" controlId="contract">
                <Form.Label>Contract length (yrs)</Form.Label>
                <Form.Control
                  type="text"
                  value={site.tariffConfig.supplier.contractLength}
                  onChange={(e) =>
                    setSite({
                      tariffConfig: {
                        ...site.tariffConfig!,
                        supplier: {
                          ...site.tariffConfig!.supplier,
                          contractLength: e.target.value,
                        },
                      },
                    })
                  }
                  isInvalid={!!errors[`tariffConfig.supplier.contractLength`]}
                />
                <Form.Control.Feedback type="invalid">
                  {errors[`tariffConfig.supplier.contractLength`]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col sm={3}>
              <Form.Group className="mb-3" controlId="ppaStartPoint">
                <Form.Label>PPA starting point (years)</Form.Label>
                <Form.Control
                  type="text"
                  value={site.tariffConfig.supplier.ppaStartPoint}
                  onChange={(e) =>
                    setSite({
                      tariffConfig: {
                        supplier: {
                          ppaStartPoint: e.target.value,
                        },
                      },
                    })
                  }
                  isInvalid={!!errors[`tariffConfig.supplier.ppaStartPoint`]}
                />
                <Form.Control.Feedback type="invalid">
                  {errors[`tariffConfig.supplier.ppaStartPoint`]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <TimedTariffTariffControl
              errors={errors}
              setSite={setSite}
              tariffLabel={`Off-peak/night (${currencySymbol(siteCalculations.currencyCode)}/kWh)`}
              tariffFieldName="peakOffPeak"
              tariff={site.tariffConfig.supplier.peakOffPeak}
              rateId="offpeak"
            />
          </>
        )}
        <TimedTariffPeriodControl
          setSite={setSite}
          rateLabel="Peak"
          tariff={site.tariffConfig.supplier.peakOffPeak}
          tariffField="peakOffPeak"
          rateId="peak"
          rateRangeId="0"
        />
        {!isMarketPricePeakOffPeak && (
          <>
            <Col sm={3}>
              <Form.Label>Contract index type</Form.Label>
              <Form
                style={{
                  display: "flex",
                  fontSize: 12,
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                {Object.entries(PPA_TYPE_LABELS).map(([key, label]) => (
                  <BorderedCheckbox
                    key={`contract-index-${key}`}
                    formName="contract-index-type"
                    value={key}
                    label={label}
                    selectedOption={
                      site.tariffConfig.supplier
                        .contractIndexType as unknown as string
                    }
                    setSelectedOption={(value) =>
                      setSite({
                        tariffConfig: {
                          supplier: {
                            contractIndexType: value,
                          },
                        },
                      })
                    }
                  />
                ))}
              </Form>
            </Col>
            {site.tariffConfig.supplier.contractIndexType === "indexed" && (
              <Col sm={3}>
                <Form.Group className="mb-3" controlId="contractIndex">
                  <Form.Label>Contract index (%)</Form.Label>
                  <Form.Control
                    type="text"
                    value={site.tariffConfig.supplier.contractIndex || ""}
                    onChange={(e) =>
                      setSite({
                        tariffConfig: {
                          supplier: { contractIndex: e.target.value },
                        },
                      })
                    }
                    isInvalid={!!errors[`tariffConfig.supplier.contractIndex`]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[`tariffConfig.supplier.contractIndex`]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
          </>
        )}
        <Col sm={3}>
          <Form.Group
            className="mb-3"
            controlId="marketEnergyPriceInflationRate"
          >
            <Form.Label>Cross-contract infl. (%)</Form.Label>
            <FormInputPercentage
              value={
                site.tariffConfig.supplier.contractEnergyPriceInflationRate
              }
              onChange={(e) =>
                setSite({
                  tariffConfig: {
                    supplier: {
                      contractEnergyPriceInflationRate: e.target.value,
                    },
                  },
                })
              }
              errorKey={`tariffConfig.supplier.energyPriceInflationRate`}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3" controlId="duosInflationRate">
            <Form.Label>DUoS infl. (%)</Form.Label>
            <FormInputPercentage
              value={site.tariffConfig.supplier.duosInflationRate}
              onChange={(e) =>
                setSite({
                  tariffConfig: {
                    supplier: {
                      duosInflationRate: e.target.value,
                    },
                  },
                })
              }
              errorKey={`tariffConfig.supplier.duosInflationRate`}
            />
          </Form.Group>
        </Col>
        <Col sm={3}>
          <Form.Group className="mb-3" controlId="voltage">
            <Form.Label>Voltage</Form.Label>
            <Form.Select
              value={site.tariffConfig.supplier.voltage}
              onChange={(e) =>
                setSite({
                  tariffConfig: {
                    ...site.tariffConfig!,
                    supplier: {
                      ...site.tariffConfig!.supplier,
                      voltage: e.target.value,
                    },
                  },
                })
              }
            >
              <option value={""}>Select voltage</option>
              <option value="lv">LV</option>
              <option value="hv1">HV band 1</option>
              <option value="hv2">HV band 2</option>
              <option value="hv3">HV band 3</option>
              <option value="hv4">HV band 4</option>
              <option value="hv-unknown">HV unknown band</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <CollapsibleCard
        title={"Passthrough: Impactable charges"}
        className="mt-2"
        initialOpen={false}
      >
        <Row className="d-flex flex-wrap align-items-end">
          {!isBulkEdit && (
            <>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="annualDemandMSP">
                  <Form.Label>Demand @ MSP (kWh)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatNumber(
                      siteCalculations.energyFlowAnnual?.demand || 0,
                      2,
                      2
                    )}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="annualDemandGSP">
                  <Form.Label>Demand @ GSP (kWh)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatNumber(
                      (siteCalculations.energyFlowAnnual?.demand || 0) *
                        (parseAsNumber(
                          site.tariffConfig.supplier.tariffPointFactorGsp
                        ) || 1),
                      2,
                      2
                    )}
                    disabled
                  />
                </Form.Group>
              </Col>
              <Col sm={4}>
                <Form.Group className="mb-3" controlId="annualDemandNBP">
                  <Form.Label>Demand @ NBP (kWh)</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatNumber(
                      (siteCalculations.energyFlowAnnual?.demand || 0) *
                        (parseAsNumber(
                          site.tariffConfig.supplier.tariffPointFactorGsp
                        ) || 1) *
                        (parseAsNumber(
                          site.tariffConfig.supplier.tariffPointFactorNbp
                        ) || 1),
                      2,
                      2
                    )}
                    disabled
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <TariffPointFactorControl
            setSite={setSite}
            errors={errors}
            supplier={site.tariffConfig.supplier}
            label="Line loss factor (LLF/LAF)"
            tariffPointFieldName="tariffPointFactorGsp"
          />
          <FixedTariffTariffControl
            tariff={
              site.tariffConfig.supplier.standardFixedTariffs["Charge @ GSP"]
            }
            label={"GSP tariff (£/kWh)"}
            setSite={setSite}
          />
          <FixedTariffTariffControl
            tariff={
              site.tariffConfig.supplier.standardFixedTariffs[
                "Distribution losses"
              ]
            }
            label={"Dist. losses (£/kWh)"}
            setSite={setSite}
          />
          <TariffPointFactorControl
            setSite={setSite}
            errors={errors}
            supplier={site.tariffConfig.supplier}
            label="Trans. loss factor"
            tariffPointFieldName="tariffPointFactorNbp"
          />
          <FixedTariffTariffControl
            tariff={
              site.tariffConfig.supplier.standardFixedTariffs[
                "Transmission losses"
              ]
            }
            label={"Trans. losses (£/kWh)"}
            setSite={setSite}
          />
        </Row>
        <Row className="d-flex flex-wrap align-items-start">
          <TimedTariffTariffControl
            errors={errors}
            setSite={setSite}
            tariffLabel={`DUoS 1/Red (${currencySymbol(siteCalculations.currencyCode)}/kWh)`}
            tariffFieldName="duos"
            tariff={site.tariffConfig.supplier.duos}
            rateId="red"
            size={4}
            showRatePeriods={true}
          />
          <TimedTariffTariffControl
            errors={errors}
            setSite={setSite}
            tariffLabel={`DUoS 2/Amber (${currencySymbol(siteCalculations.currencyCode)}/kWh)`}
            tariffFieldName="duos"
            tariff={site.tariffConfig.supplier.duos}
            rateId="amber"
            size={4}
            showRatePeriods={true}
          />
          <TimedTariffTariffControl
            errors={errors}
            setSite={setSite}
            tariffLabel={`DUoS 3/Green (${currencySymbol(siteCalculations.currencyCode)}/kWh)`}
            tariffFieldName="duos"
            tariff={site.tariffConfig.supplier.duos}
            rateId="green"
            size={4}
          />
          <SingleRateTimedTariffTariffControl
            errors={errors}
            setSite={setSite}
            tariffLabel={`Capacity market (£/kWh)`}
            tariff={
              site.tariffConfig.supplier.standardSingleRateTimedTariffs[
                "Capacity market"
              ]
            }
            showRatePeriods={true}
            size={4}
          />
          {[
            { name: "Climate Change Levy", label: "CCL (£/kWh)" },
            { name: "Renewables Obligation", label: "RO (£/kWh)" },
            { name: "FiT", label: "FiT (£/kWh)" },
            { name: "AAHEDC", label: "AAHEDC (£/kWh)" },
            { name: "BSUoS", label: "BSUoS (£/kWh)" },
            { name: "CfD FiT", label: "CfD FiT (£/kWh)" },
          ].map((tariff, index) => (
            <FixedTariffTariffControl
              key={index}
              tariff={
                site.tariffConfig.supplier.standardFixedTariffs[tariff.name]
              }
              label={tariff.label}
              setSite={setSite}
              size={4}
            />
          ))}
        </Row>
        <Row className="d-flex flex-wrap align-items-end">
          {customFixedTariffs.map((tariff, index) => (
            <CustomFixedTariffControl
              key={customFixedTariffsState[index].key}
              tariff={tariff}
              tariffState={customFixedTariffsState[index]}
              setTariff={(updatedTariff) => {
                setCustomFixedTariffs((prev) =>
                  prev.map((t, i) => (i === index ? updatedTariff : t))
                );
              }}
              removeFixedTariff={() => {
                setCustomFixedTariffs((prev) =>
                  prev.filter((_, i) => i !== index)
                );
                setCustomFixedTariffsState((prev) =>
                  prev.filter((_, i) => i !== index)
                );
              }}
            />
          ))}
        </Row>
        <Row className="d-flex flex-wrap align-items-end">
          <Col sm={8}></Col>
          <Col sm={4}>
            {" "}
            <div
              style={{
                textAlign: "left",
                color: "rgb(13, 110, 253)",
                cursor: "pointer",
              }}
            >
              <a
                onClick={() => {
                  setCustomFixedTariffs((prev) => [
                    ...prev,
                    {
                      tariffName: "",
                      tariff: "",
                      tariffPoint: TariffPoint.MSP,
                      isLossesCharge: false,
                    },
                  ]);
                  setCustomFixedTariffsState((prev) => [
                    ...prev,
                    {
                      isInserted: false,
                      insertedIndex: -1,
                      key: `custom-${prev.length}`,
                      tariffLabel: "custom",
                    },
                  ]);
                }}
              >
                Add custom charge
              </a>
            </div>
          </Col>
        </Row>
      </CollapsibleCard>
      <CollapsibleCard
        title={"Passthrough: Standing charges"}
        className="mt-2"
        initialOpen={false}
      >
        <Row className="d-flex flex-wrap align-items-end">
          {[
            {
              name: "DUoS Availability/Capacity",
              volumeLabel: "Available capacity (kVA)",
              label: "DUoS A/C (£/kVA/day)",
            },
            {
              name: "DUoS Standing",
              label: "DUoS standing (£/day)",
            },
            {
              name: "DUoS Excess availability",
              volumeLabel: "Excess availability (kVA)",
              label: "DUoS excess (£/kVA/day)",
            },
            {
              name: "TNUoS fixed/residual",
              label: "TNUoS fixed/residual (£/day)",
            },
          ].map((charge) => (
            <React.Fragment key={charge.name}>
              {charge.volumeLabel && (
                <StandingChargeVolumeControl
                  standingCharge={
                    site.tariffConfig.supplier.standardStandingCharges[
                      charge.name
                    ]
                  }
                  label={charge.volumeLabel}
                  setSite={setSite}
                  size={4}
                />
              )}
              <StandingChargeTariffControl
                standingCharge={
                  site.tariffConfig.supplier.standardStandingCharges[
                    charge.name
                  ]
                }
                label={charge.label}
                setSite={setSite}
                size={4}
              />
            </React.Fragment>
          ))}
        </Row>
        <Row className="d-flex flex-wrap align-items-end">
          {customStandingCharges.map((charge, index) => (
            <CustomStandingChargeControl
              key={customStandingChargesState[index].key}
              standingCharge={charge}
              standingChargeState={customStandingChargesState[index]}
              setStandingCharge={(updatedCharge) => {
                setCustomStandingCharges((prev) =>
                  prev.map((c, i) => (i === index ? updatedCharge : c))
                );
              }}
              removeStandingCharge={() => {
                setCustomStandingCharges((prev) =>
                  prev.filter((_, i) => i !== index)
                );
                setCustomStandingChargesState((prev) =>
                  prev.filter((_, i) => i !== index)
                );
              }}
            />
          ))}
        </Row>
        <Row className="d-flex flex-wrap align-items-end">
          <Col sm={8}></Col>
          <Col sm={4}>
            <div
              style={{
                textAlign: "left",
                color: "rgb(13, 110, 253)",
                cursor: "pointer",
              }}
            >
              <a
                onClick={() => {
                  setCustomStandingCharges((prev) => [
                    ...prev,
                    {
                      chargeName: "",
                      tariff: "",
                      chargeType: StandingChargeType.PER_DAY,
                    },
                  ]);
                  setCustomStandingChargesState((prev) => [
                    ...prev,
                    {
                      isInserted: false,
                      insertedIndex: -1,
                      key: `charge-${prev.length}`,
                      tariffLabel: "custom",
                    },
                  ]);
                }}
              >
                Add custom charge
              </a>
            </div>
          </Col>
        </Row>
      </CollapsibleCard>
      {!isBulkEdit && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            alignItems: "baseline",
            flexWrap: "wrap",
          }}
        >
          <Form.Group controlId={"monthSelect"}>
            <Form.Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {[...Array(12).keys()].map((month) => (
                <option key={month} value={month + 1}>
                  {new Date(0, month).toLocaleString("default", {
                    month: "long",
                  })}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Button
            className="mt-3"
            variant="inrangesecondary"
            onClick={() =>
              fetchSiteReportPreview.mutate({
                reportName: "energy_bill_savings",
                fileName: "energy-bill-savings",
                siteName: site.name,
                options: {
                  month: selectedMonth,
                  debug: debugEnabled,
                },
                payload: {
                  site: buildSiteCalculationPayload({
                    site: site as unknown as Partial<Site>,
                    clearNonCalcFields: false,
                  }),
                },
              })
            }
          >
            Download month analysis
          </Button>
          <Button
            className="mt-3"
            variant="inrangesecondary"
            onClick={() =>
              fetchSiteReportPreview.mutate({
                reportName: "energy_bill_savings",
                fileName: "energy-bill-savings",
                siteName: site.name,
                options: {
                  debug: debugEnabled,
                },
                payload: {
                  site: buildSiteCalculationPayload({
                    site: site as unknown as Partial<Site>,
                    clearNonCalcFields: false,
                  }),
                },
              })
            }
          >
            Download PPA duration analysis
          </Button>
          <Form.Group controlId="debugCheck">
            <Form.Check
              type="switch"
              label="Debug"
              checked={debugEnabled}
              onChange={(e) => setDebugEnabled(e.target.checked)}
            />
          </Form.Group>
        </div>
      )}
    </CollapsibleCard>
  );
};

export default SupplierTariff;
